import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { ASSETS } from '../config/schools';
import { 
  FaNewspaper, FaGraduationCap, FaRobot, FaChartLine, 
  FaUsers, FaGamepad, FaBook, FaKeyboard, FaUserTie,
  FaArrowRight, FaCheckCircle, FaEnvelope, FaSchool,
  FaGlobe, FaLightbulb, FaMicrophone, FaSearch, FaPencilAlt,
  FaTrophy, FaCreditCard, FaPaypal, FaTimes, FaCheck,
  FaUserShield, FaLock, FaPhone, FaUser, FaCalendarAlt
} from 'react-icons/fa';
import api from '../utils/api';
import { setToken, setUser } from '../utils/auth';

// PayPal Client ID - Mode Sandbox pour tests
const PAYPAL_CLIENT_ID = "sb"; // "sb" pour sandbox, remplacer par vrai ID en production

function LandingPage() {
  const navigate = useNavigate();
  const [showContactModal, setShowContactModal] = useState(false);
  const [showConcoursModal, setShowConcoursModal] = useState(false);
  const [showEducatorDemoModal, setShowEducatorDemoModal] = useState(false);
  const [concoursStep, setConcoursStep] = useState(1); // 1: formulaire, 2: paiement, 3: succès
  const [contactForm, setContactForm] = useState({
    schoolName: '',
    contactName: '',
    email: '',
    phone: '',
    message: '',
    role: 'professor'
  });
  const [concoursForm, setConcoursForm] = useState({
    fullName: '',
    email: '',
    password: '',
    examDate: '',
    targetSchools: []
  });
  const [educatorForm, setEducatorForm] = useState({
    firstName: '',
    lastName: '',
    function: 'professor',
    email: '',
    phone: '',
    password: '',
    schoolName: '',
    schoolAddress: '',
    accreditationNumber: ''
  });
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);
  const [concoursSubmitting, setConcoursSubmitting] = useState(false);
  const [concoursError, setConcoursError] = useState('');
  const [educatorSubmitting, setEducatorSubmitting] = useState(false);
  const [educatorSuccess, setEducatorSuccess] = useState(false);
  const [educatorError, setEducatorError] = useState('');
  const [schoolInput, setSchoolInput] = useState('');

  const targetSchoolsList = [
    "ESJ Lille", "CFJ Paris", "IPJ Paris-Dauphine", "Sciences Po Paris",
    "CELSA", "IJBA Bordeaux", "CUEJ Strasbourg", "EJC Paris",
    "EDJ Nice", "IFP Paris", "Autre"
  ];

  const modules = [
    {
      category: "Quiz & Culture Générale",
      icon: FaNewspaper,
      color: "#E63946",
      items: [
        { name: "Quiz Quotidien", desc: "30 questions d'actualité générées chaque jour", icon: FaNewspaper },
        { name: "Qui est Qui ?", desc: "Personnalités politiques, économiques, médiatiques", icon: FaUserTie },
        { name: "Histoire de la Presse", desc: "De Gutenberg aux réseaux sociaux, 7 époques", icon: FaBook }
      ]
    },
    {
      category: "Compétences Rédactionnelles",
      icon: FaGraduationCap,
      color: "#FFB703",
      items: [
        { name: "Grammaire & Orthographe", desc: "Exercices ciblés, écriture irréprochable", icon: FaPencilAlt },
        { name: "Cours SEO", desc: "Optimisez vos articles pour le web", icon: FaSearch },
        { name: "Économie des Médias", desc: "Modèles économiques, concentration, numérique", icon: FaChartLine }
      ]
    },
    {
      category: "Pratique du Métier",
      icon: FaMicrophone,
      color: "#2563eb",
      items: [
        { name: "Simulation d'Interview", desc: "Entraînez-vous avec des personnalités fictives", icon: FaMicrophone },
        { name: "Pitch d'Angles", desc: "Proposez angles percutants, sources pertinentes", icon: FaLightbulb }
      ]
    },
    {
      category: "Outils & Jeux",
      icon: FaGamepad,
      color: "#10B981",
      items: [
        { name: "Dactylo AZERTY", desc: "11 niveaux pour maîtriser la frappe rapide", icon: FaKeyboard }
      ]
    }
  ];

  const benefits = [
    { icon: FaUsers, title: "Créé par des pros", desc: "Journalistes professionnels, professeurs expérimentés" },
    { icon: FaGamepad, title: "Pédagogie addictive", desc: "Gamification, challenges : vos élèves en redemandent" },
    { icon: FaTrophy, title: "Hall of Fame", desc: "Classements, défis entre élèves, motivation décuplée" },
    { icon: FaChartLine, title: "Suivi en temps réel", desc: "Tableaux de bord étudiants, professeurs, direction" }
  ];

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactSubmitting(true);
    try {
      // Enregistrer et envoyer l'email via l'API backend (iCloud SMTP)
      await api.post('/contact', contactForm);
      
      setContactSuccess(true);
      setTimeout(() => {
        setShowContactModal(false);
        setContactSuccess(false);
        setContactForm({ schoolName: '', contactName: '', email: '', phone: '', message: '', role: 'professor' });
      }, 2000);
    } catch (error) {
      alert('Erreur lors de l\'envoi. Réessayez ou contactez-nous directement à contact@dailyglobe.school');
    } finally {
      setContactSubmitting(false);
    }
  };

  const handleEducatorSubmit = async (e) => {
    e.preventDefault();
    setEducatorSubmitting(true);
    setEducatorError('');
    try {
      // L'API backend gère l'envoi d'email automatiquement
      const response = await api.post('/educator-demo/signup', educatorForm);
      setToken(response.data.token);
      setUser(response.data.user);
      
      setEducatorSuccess(true);
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (error) {
      setEducatorError(error.response?.data?.detail || 'Erreur lors de l\'inscription');
    } finally {
      setEducatorSubmitting(false);
    }
  };

  const handleConcoursSubmit = async (e) => {
    e.preventDefault();
    setConcoursSubmitting(true);
    setConcoursError('');
    try {
      await api.post('/concours/signup', concoursForm);
      setConcoursStep(2); // Passer à l'étape paiement
    } catch (error) {
      setConcoursError(error.response?.data?.detail || 'Erreur lors de l\'inscription');
    } finally {
      setConcoursSubmitting(false);
    }
  };

  const handlePayPalSuccess = async (orderId) => {
    try {
      // L'API backend gère l'envoi d'email automatiquement
      const response = await api.post('/concours/confirm-payment', {
        email: concoursForm.email,
        paypalOrderId: orderId
      });
      setToken(response.data.token);
      setUser(response.data.user);
      
      setConcoursStep(3); // Succès
      setTimeout(() => {
        navigate('/concours-dashboard');
      }, 2500);
    } catch (error) {
      setConcoursError('Erreur lors de la confirmation du paiement. Contactez-nous avec votre reçu PayPal.');
    }
  };

  const addTargetSchool = (school) => {
    if (school && !concoursForm.targetSchools.includes(school)) {
      setConcoursForm({
        ...concoursForm,
        targetSchools: [...concoursForm.targetSchools, school]
      });
    }
    setSchoolInput('');
  };

  const removeTargetSchool = (school) => {
    setConcoursForm({
      ...concoursForm,
      targetSchools: concoursForm.targetSchools.filter(s => s !== school)
    });
  };

  const resetConcoursModal = () => {
    setShowConcoursModal(false);
    setConcoursStep(1);
    setConcoursForm({ fullName: '', email: '', password: '', examDate: '', targetSchools: [] });
    setConcoursError('');
  };

  return (
    <div className="min-h-screen" style={{ background: '#F8F4E3' }}>
      {/* Hero Section */}
      <header className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #1B4965 0%, #2D5A7B 100%)' }}>
        <div className="absolute inset-0 halftone-bg opacity-10"></div>
        
        <nav className="container mx-auto px-4 py-4 relative z-10">
          {/* Mobile: Logo centré, boutons en dessous */}
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3">
            <div className="flex items-center justify-center md:justify-start space-x-2">
              <img src={ASSETS.DAILYGLOBE_LOGO} alt="DailyGlobe" className="w-10 h-10 md:w-12 md:h-12 object-contain" />
              <span className="text-lg md:text-xl font-bold text-white" style={{ fontFamily: 'Bebas Neue' }}>DAILYGLOBE SCHOOL</span>
            </div>
            <div className="flex items-center justify-center gap-2 flex-wrap">
              <button 
                onClick={() => setShowEducatorDemoModal(true)}
                className="comic-button bg-gradient-to-r from-green-500 to-emerald-600 text-white border-2 border-green-700 text-xs md:text-sm px-2 md:px-4 py-1 md:py-2"
                data-testid="educator-demo-nav-button"
              >
                <FaUserShield className="inline mr-1" /> <span className="hidden sm:inline">Démo</span> Enseignants
              </button>
              <button 
                onClick={() => setShowConcoursModal(true)}
                className="comic-button bg-gradient-to-r from-yellow-400 to-orange-500 text-white border-2 border-yellow-600 text-xs md:text-sm px-2 md:px-4 py-1 md:py-2"
                data-testid="concours-nav-button"
              >
                <FaTrophy className="inline mr-1" /> Concours Grandes Écoles
              </button>
              {/* Connexion - lien discret */}
              <button 
                onClick={() => navigate('/login')}
                className="text-white/90 hover:text-white text-sm font-medium px-3 py-2 hover:underline transition-all"
                data-testid="login-nav-button"
              >
                Connexion →
              </button>
            </div>
          </div>
        </nav>

        <div className="container mx-auto px-4 py-16 md:py-24 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", duration: 0.8 }}
              className="inline-block mb-6 px-4 py-2 bg-yellow-400 text-black font-bold rounded-full border-2 border-black"
            >
              Plateforme SaaS pour écoles de journalisme
            </motion.div>
            <motion.h1 
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              className="text-4xl md:text-6xl mb-6"
              style={{ fontFamily: 'Bangers, cursive', color: 'white', textShadow: '4px 4px 0px rgba(0,0,0,0.3)' }}
            >
              LA PLATEFORME PÉDAGOGIQUE DE VOTRE ÉCOLE DE JOURNALISME
            </motion.h1>
            <motion.p 
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-xl md:text-2xl text-white/90 mb-8"
              style={{ fontFamily: 'Bebas Neue', letterSpacing: '1px' }}
            >
              Des cours conçus par des journalistes professionnels et des professeurs expérimentés.
              <br />Une pédagogie ludique qui donne envie d'apprendre.
            </motion.p>
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <button 
                onClick={() => navigate('/login')}
                className="comic-button comic-button-red text-xl px-8 py-4"
                data-testid="cta-demo-button"
              >
                Essayer la démo gratuite <FaArrowRight className="inline ml-2" />
              </button>
              <button 
                onClick={() => setShowContactModal(true)}
                className="comic-button bg-white text-gray-800 text-xl px-8 py-4"
                data-testid="cta-contact-button"
              >
                <FaSchool className="inline mr-2" /> Équiper mon école
              </button>
            </motion.div>
          </div>
        </div>

        {/* Wave separator */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#F8F4E3"/>
          </svg>
        </div>
      </header>

      {/* Benefits Section */}
      <section className="container mx-auto px-4 py-16">
        <h2 className="text-2xl md:text-3xl text-center mb-8" style={{ fontFamily: 'Bangers, cursive', color: '#1B4965' }}>
          POURQUOI CHOISIR DAILYGLOBE SCHOOL ?
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              initial={{ y: 50, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ delay: index * 0.1 }}
              viewport={{ once: true }}
              className="comic-panel text-center"
            >
              <benefit.icon className="text-4xl mx-auto mb-3" style={{ color: '#E63946' }} />
              <h3 className="comic-text text-lg mb-2">{benefit.title}</h3>
              <p className="text-sm text-gray-600">{benefit.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Multi-School Section */}
      <section className="py-16" style={{ background: '#e8e4d3' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl text-center mb-8" style={{ fontFamily: 'Bangers, cursive', color: '#1B4965' }}>
              UNE PLATEFORME, PLUSIEURS ÉCOLES
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                className="comic-panel"
              >
                <FaGlobe className="text-4xl mx-auto mb-3 text-blue-600" />
                <h3 className="font-bold mb-2">Actualités localisées</h3>
                <p className="text-sm text-gray-600">Quiz générés selon le pays de votre école (France, Belgique, Suisse, Canada...)</p>
              </motion.div>
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.1 }}
                viewport={{ once: true }}
                className="comic-panel"
              >
                <FaSchool className="text-4xl mx-auto mb-3 text-green-600" />
                <h3 className="font-bold mb-2">Branding personnalisé</h3>
                <p className="text-sm text-gray-600">Logo de votre école, couleurs, sous-domaine dédié</p>
              </motion.div>
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.2 }}
                viewport={{ once: true }}
                className="comic-panel"
              >
                <FaEnvelope className="text-4xl mx-auto mb-3 text-purple-600" />
                <h3 className="font-bold mb-2">Notifications automatiques</h3>
                <p className="text-sm text-gray-600">Votre référent reçoit les annonces de nouvelles fonctionnalités</p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Modules Section */}
      <section id="modules" className="py-16" style={{ background: '#1B4965' }}>
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl text-center mb-12" style={{ fontFamily: 'Bangers, cursive', color: 'white', textShadow: '3px 3px 0px rgba(0,0,0,0.3)' }}>
            10 MODULES PÉDAGOGIQUES
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {modules.map((category, index) => (
              <motion.div
                key={index}
                initial={{ x: index % 2 === 0 ? -50 : 50, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="comic-panel"
              >
                <div className="flex items-center mb-4">
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center mr-4"
                    style={{ background: category.color }}
                  >
                    <category.icon className="text-2xl text-white" />
                  </div>
                  <h3 className="comic-text text-xl" style={{ color: category.color }}>{category.category}</h3>
                </div>
                <ul className="space-y-3">
                  {category.items.map((item, idx) => (
                    <li key={idx} className="flex items-start">
                      <FaCheckCircle className="text-green-500 mt-1 mr-2 flex-shrink-0" />
                      <div>
                        <span className="font-bold">{item.name}</span>
                        <p className="text-sm text-gray-600">{item.desc}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Concours Section */}
      <section className="py-16" style={{ background: 'linear-gradient(135deg, #FF6B35 0%, #E63946 100%)' }}>
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center text-white"
          >
            <FaTrophy className="text-6xl mx-auto mb-4 text-yellow-300" />
            <h2 className="text-3xl md:text-4xl mb-4" style={{ fontFamily: 'Bangers, cursive', textShadow: '3px 3px 0px rgba(0,0,0,0.3)' }}>
              PRÉPAREZ LES CONCOURS DES ÉCOLES DE JOURNALISME
            </h2>
            <p className="text-lg mb-6 opacity-90">
              Vous êtes étudiant et vous préparez les concours d'entrée aux grandes écoles de journalisme ?
              Accédez à tous nos modules pour vous entraîner efficacement.
            </p>
            <button 
              onClick={() => setShowConcoursModal(true)}
              className="comic-button bg-yellow-400 text-black text-xl px-8 py-4 border-2 border-black"
              data-testid="cta-concours-button"
            >
              <FaPaypal className="inline mr-2" /> S'inscrire (via PayPal)
            </button>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="container mx-auto px-4 py-16 text-center">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 1 }}
          viewport={{ once: true }}
          className="comic-panel max-w-2xl mx-auto"
          style={{ background: 'linear-gradient(135deg, #FFB703 0%, #FB8500 100%)' }}
        >
          <h2 className="text-3xl md:text-4xl mb-4" style={{ fontFamily: 'Bangers, cursive', color: 'white', textShadow: '3px 3px 0px rgba(0,0,0,0.3)' }}>
            PRÊT À TRANSFORMER VOTRE FORMATION ?
          </h2>
          <p className="text-lg text-white/90 mb-6" style={{ fontFamily: 'Bebas Neue', letterSpacing: '1px' }}>
            Essayez gratuitement avec le compte démo ou contactez-nous pour équiper votre école
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => navigate('/login')}
              className="comic-button comic-button-navy text-lg px-6 py-3"
              data-testid="cta-final-button"
            >
              Accéder à la démo
            </button>
            <button 
              onClick={() => setShowContactModal(true)}
              className="comic-button bg-white text-gray-800 text-lg px-6 py-3"
              data-testid="contact-final-button"
            >
              <FaEnvelope className="inline mr-2" /> Nous contacter
            </button>
          </div>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="border-t-4 border-black py-8" style={{ background: '#1B4965' }}>
        <div className="container mx-auto px-4 text-center text-white/80">
          <div className="flex items-center justify-center mb-4">
            <img src={ASSETS.DAILYGLOBE_LOGO} alt="DailyGlobe" className="w-8 h-8 object-contain mr-2" />
            <span style={{ fontFamily: 'Bebas Neue' }}>DAILYGLOBE SCHOOL</span>
          </div>
          <p className="text-sm">La plateforme pédagogique pour former les journalistes de demain</p>
          <p className="text-xs mt-2 opacity-60">contact@dailyglobe.school</p>
          <p className="text-xs mt-1 opacity-80">
            <a href="tel:0663212234" className="hover:underline">📞 06 63 21 22 34</a>
          </p>
        </div>
      </footer>

      {/* Contact Modal */}
      <AnimatePresence>
        {showContactModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowContactModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="comic-panel max-w-lg w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              data-testid="contact-modal"
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="comic-title text-2xl" style={{ color: '#1B4965' }}>
                  <FaSchool className="inline mr-2" /> Équiper mon école
                </h2>
                <button onClick={() => setShowContactModal(false)} className="text-gray-500 hover:text-gray-700">
                  <FaTimes className="text-xl" />
                </button>
              </div>

              {contactSuccess ? (
                <div className="text-center py-8">
                  <FaCheck className="text-6xl text-green-500 mx-auto mb-4" />
                  <p className="text-xl font-bold text-green-600">Message envoyé !</p>
                  <p className="text-gray-600">Nous vous recontacterons rapidement.</p>
                </div>
              ) : (
                <form onSubmit={handleContactSubmit} className="space-y-4">
                  <div>
                    <label className="block font-bold mb-1">Je suis *</label>
                    <select
                      value={contactForm.role}
                      onChange={(e) => setContactForm({ ...contactForm, role: e.target.value })}
                      className="w-full p-3 border-2 border-black rounded"
                      required
                    >
                      <option value="professor">Professeur</option>
                      <option value="director">Directeur d'école</option>
                      <option value="admin">Responsable pédagogique</option>
                      <option value="other">Autre</option>
                    </select>
                  </div>
                  <div>
                    <label className="block font-bold mb-1">Nom de l'établissement *</label>
                    <input
                      type="text"
                      value={contactForm.schoolName}
                      onChange={(e) => setContactForm({ ...contactForm, schoolName: e.target.value })}
                      className="w-full p-3 border-2 border-black rounded"
                      placeholder="Ex: ISCPA Lyon"
                      required
                      data-testid="contact-school-name"
                    />
                  </div>
                  <div>
                    <label className="block font-bold mb-1">Votre nom *</label>
                    <input
                      type="text"
                      value={contactForm.contactName}
                      onChange={(e) => setContactForm({ ...contactForm, contactName: e.target.value })}
                      className="w-full p-3 border-2 border-black rounded"
                      placeholder="Prénom Nom"
                      required
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <label className="block font-bold mb-1">Email professionnel *</label>
                    <input
                      type="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      className="w-full p-3 border-2 border-black rounded"
                      placeholder="vous@ecole.fr"
                      required
                      data-testid="contact-email"
                    />
                  </div>
                  <div>
                    <label className="block font-bold mb-1">Téléphone</label>
                    <input
                      type="tel"
                      value={contactForm.phone}
                      onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                      className="w-full p-3 border-2 border-black rounded"
                      placeholder="06 XX XX XX XX"
                      data-testid="contact-phone"
                    />
                  </div>
                  <div>
                    <label className="block font-bold mb-1">Message</label>
                    <textarea
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      className="w-full p-3 border-2 border-black rounded h-24"
                      placeholder="Parlez-nous de votre projet..."
                      data-testid="contact-message"
                    />
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                    <p className="text-sm text-blue-800">
                      <span className="font-semibold">📞 Une question, un accès test :</span>
                      <a href="tel:0663212234" className="ml-2 font-bold text-blue-600 hover:underline">
                        06 63 21 22 34
                      </a>
                    </p>
                  </div>
                  
                  <button
                    type="submit"
                    disabled={contactSubmitting}
                    className="comic-button comic-button-navy w-full"
                    data-testid="contact-submit"
                  >
                    {contactSubmitting ? 'Envoi...' : 'Envoyer ma demande'}
                  </button>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Concours Modal - Nouveau avec formulaire et PayPal */}
      <AnimatePresence>
        {showConcoursModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={resetConcoursModal}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="comic-panel max-w-lg w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              data-testid="concours-modal"
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="comic-title text-2xl" style={{ color: '#E63946' }}>
                  <FaTrophy className="inline mr-2 text-yellow-500" /> Prépa Concours Grandes Écoles
                </h2>
                <button onClick={resetConcoursModal} className="text-gray-500 hover:text-gray-700">
                  <FaTimes className="text-xl" />
                </button>
              </div>

              {/* Indicateur d'étapes */}
              <div className="flex justify-center mb-6">
                {[1, 2, 3].map((step) => (
                  <div key={step} className="flex items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm
                      ${concoursStep >= step ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-500'}`}>
                      {step === 3 ? <FaCheck /> : step}
                    </div>
                    {step < 3 && (
                      <div className={`w-12 h-1 mx-1 ${concoursStep > step ? 'bg-orange-500' : 'bg-gray-200'}`}></div>
                    )}
                  </div>
                ))}
              </div>

              {/* Étape 1: Formulaire d'inscription */}
              {concoursStep === 1 && (
                <>
                  <p className="text-gray-600 mb-4 text-sm text-center">
                    Préparez les concours d'entrée aux écoles de journalisme avec un entraînement quotidien personnalisé.
                  </p>

                  {concoursError && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded mb-4">
                      {concoursError}
                    </div>
                  )}

                  <form onSubmit={handleConcoursSubmit} className="space-y-4">
                    <div>
                      <label className="block font-bold mb-1">
                        <FaUser className="inline mr-1" /> Nom complet *
                      </label>
                      <input
                        type="text"
                        value={concoursForm.fullName}
                        onChange={(e) => setConcoursForm({ ...concoursForm, fullName: e.target.value })}
                        className="w-full p-3 border-2 border-black rounded"
                        placeholder="Prénom Nom"
                        required
                        data-testid="concours-fullname"
                      />
                    </div>

                    <div>
                      <label className="block font-bold mb-1">
                        <FaEnvelope className="inline mr-1" /> Email *
                      </label>
                      <input
                        type="email"
                        value={concoursForm.email}
                        onChange={(e) => setConcoursForm({ ...concoursForm, email: e.target.value })}
                        className="w-full p-3 border-2 border-black rounded"
                        placeholder="votre@email.com"
                        required
                        data-testid="concours-email"
                      />
                    </div>

                    <div>
                      <label className="block font-bold mb-1">
                        <FaLock className="inline mr-1" /> Mot de passe *
                      </label>
                      <input
                        type="password"
                        value={concoursForm.password}
                        onChange={(e) => setConcoursForm({ ...concoursForm, password: e.target.value })}
                        className="w-full p-3 border-2 border-black rounded"
                        placeholder="Minimum 6 caractères"
                        minLength={6}
                        required
                        data-testid="concours-password"
                      />
                    </div>

                    <div>
                      <label className="block font-bold mb-1">
                        <FaCalendarAlt className="inline mr-1 text-red-500" /> Date de votre concours *
                      </label>
                      <input
                        type="date"
                        value={concoursForm.examDate}
                        onChange={(e) => setConcoursForm({ ...concoursForm, examDate: e.target.value })}
                        className="w-full p-3 border-2 border-black rounded"
                        min={new Date().toISOString().split('T')[0]}
                        required
                        data-testid="concours-exam-date"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Un compte à rebours personnalisé sera affiché dans votre tableau de bord !
                      </p>
                    </div>

                    <div>
                      <label className="block font-bold mb-1">
                        <FaGraduationCap className="inline mr-1" /> Écoles visées
                      </label>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {concoursForm.targetSchools.map((school) => (
                          <span
                            key={school}
                            className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm flex items-center gap-1"
                          >
                            {school}
                            <button
                              type="button"
                              onClick={() => removeTargetSchool(school)}
                              className="text-orange-600 hover:text-red-600"
                            >
                              <FaTimes className="text-xs" />
                            </button>
                          </span>
                        ))}
                      </div>
                      <select
                        value={schoolInput}
                        onChange={(e) => {
                          if (e.target.value) addTargetSchool(e.target.value);
                        }}
                        className="w-full p-3 border-2 border-black rounded"
                        data-testid="concours-target-school"
                      >
                        <option value="">Ajouter une école...</option>
                        {targetSchoolsList.filter(s => !concoursForm.targetSchools.includes(s)).map((school) => (
                          <option key={school} value={school}>{school}</option>
                        ))}
                      </select>
                    </div>

                    <div className="bg-gradient-to-r from-yellow-100 to-orange-100 p-4 rounded-lg border-2 border-yellow-400">
                      <p className="text-2xl font-bold text-gray-800 mb-1 text-center">4,99 € / mois</p>
                      <p className="text-sm text-gray-600 text-center">Accès illimité à tous les modules</p>
                    </div>

                    <button
                      type="submit"
                      disabled={concoursSubmitting}
                      className="comic-button w-full text-lg py-3"
                      style={{ background: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)', color: 'white' }}
                      data-testid="concours-submit"
                    >
                      {concoursSubmitting ? 'Création du compte...' : 'Continuer vers le paiement →'}
                    </button>
                  </form>
                </>
              )}

              {/* Étape 2: Paiement PayPal */}
              {concoursStep === 2 && (
                <div className="text-center">
                  <div className="mb-6">
                    <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <FaPaypal className="text-3xl text-blue-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">Finaliser votre inscription</h3>
                    <p className="text-gray-600 text-sm">
                      Compte créé pour <strong>{concoursForm.email}</strong>
                    </p>
                    {concoursForm.examDate && (
                      <p className="text-orange-600 text-sm mt-1">
                        <FaCalendarAlt className="inline mr-1" />
                        Examen prévu le {new Date(concoursForm.examDate).toLocaleDateString('fr-FR', {
                          weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
                        })}
                      </p>
                    )}
                  </div>

                  {concoursError && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded mb-4">
                      {concoursError}
                    </div>
                  )}

                  <div className="bg-gradient-to-r from-yellow-100 to-orange-100 p-4 rounded-lg border-2 border-yellow-400 mb-6">
                    <p className="text-3xl font-bold text-gray-800">4,99 €</p>
                    <p className="text-sm text-gray-600">Premier mois d'abonnement</p>
                  </div>

                  <PayPalScriptProvider options={{ 
                    "client-id": PAYPAL_CLIENT_ID,
                    currency: "EUR"
                  }}>
                    <PayPalButtons
                      style={{ layout: "vertical", color: "gold", shape: "rect", label: "pay" }}
                      createOrder={(data, actions) => {
                        return actions.order.create({
                          purchase_units: [{
                            amount: {
                              value: "4.99",
                              currency_code: "EUR"
                            },
                            description: "DailyGlobe School - Prépa Concours (1 mois)"
                          }]
                        });
                      }}
                      onApprove={async (data, actions) => {
                        const order = await actions.order.capture();
                        handlePayPalSuccess(order.id);
                      }}
                      onError={(err) => {
                        console.error('PayPal Error:', err);
                        setConcoursError('Erreur lors du paiement. Veuillez réessayer.');
                      }}
                      data-testid="paypal-buttons"
                    />
                  </PayPalScriptProvider>

                  <div className="mt-4 text-center">
                    <p className="text-xs text-gray-500 mb-2">ou utilisez ce lien direct :</p>
                    <a 
                      href="https://www.paypal.com/paypalme/jbgiraud/4.99EUR"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline text-sm flex items-center justify-center gap-1"
                    >
                      <FaPaypal /> PayPal.me/jbgiraud
                    </a>
                    <p className="text-xs text-gray-400 mt-2">
                      Si vous utilisez le lien direct, envoyez votre reçu à contact@dailyglobe.school
                    </p>
                  </div>

                  <button
                    onClick={() => setConcoursStep(1)}
                    className="mt-4 text-gray-500 hover:text-gray-700 text-sm"
                  >
                    ← Retour au formulaire
                  </button>
                </div>
              )}

              {/* Étape 3: Succès */}
              {concoursStep === 3 && (
                <div className="text-center py-8">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", duration: 0.5 }}
                  >
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <FaCheck className="text-4xl text-green-600" />
                    </div>
                  </motion.div>
                  <h3 className="text-2xl font-bold text-green-600 mb-2">Bienvenue dans la prépa !</h3>
                  <p className="text-gray-600 mb-4">
                    Votre compte est maintenant actif. Prêt à décrocher votre concours ?
                  </p>
                  {concoursForm.examDate && (
                    <div className="bg-orange-100 p-4 rounded-lg mb-4">
                      <p className="text-orange-800 font-bold">
                        <FaCalendarAlt className="inline mr-2" />
                        J-{Math.ceil((new Date(concoursForm.examDate) - new Date()) / (1000 * 60 * 60 * 24))} avant votre concours
                      </p>
                    </div>
                  )}
                  <p className="text-gray-500 text-sm">Redirection vers votre tableau de bord...</p>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Educator Demo Modal */}
      <AnimatePresence>
        {showEducatorDemoModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowEducatorDemoModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="comic-panel max-w-lg w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              data-testid="educator-demo-modal"
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="comic-title text-2xl" style={{ color: '#10B981' }}>
                  <FaUserShield className="inline mr-2" /> Accès Démo Enseignants
                </h2>
                <button onClick={() => setShowEducatorDemoModal(false)} className="text-gray-500 hover:text-gray-700">
                  <FaTimes className="text-xl" />
                </button>
              </div>

              {educatorSuccess ? (
                <div className="text-center py-8">
                  <FaCheck className="text-6xl text-green-500 mx-auto mb-4" />
                  <p className="text-xl font-bold text-green-600">Compte créé avec succès !</p>
                  <p className="text-gray-600">Un code de vérification a été envoyé à votre adresse email.</p>
                  <p className="text-gray-500 text-sm mt-2">Redirection vers la plateforme...</p>
                </div>
              ) : (
                <>
                  <p className="text-gray-600 mb-4 text-sm">
                    Vous êtes enseignant ou directeur d'établissement ? Testez gratuitement notre plateforme pour évaluer si elle correspond aux besoins de votre école.
                  </p>
                  
                  {/* Info box sur le processus de vérification */}
                  <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-4 rounded-r">
                    <h4 className="font-bold text-green-800 mb-2 flex items-center">
                      <FaUserShield className="mr-2" /> Processus de vérification
                    </h4>
                    <ul className="text-sm text-green-700 space-y-1">
                      <li className="flex items-start">
                        <FaCheck className="mr-2 mt-0.5 flex-shrink-0" />
                        <span><strong>Email professionnel</strong> requis (domaine de votre établissement)</span>
                      </li>
                      <li className="flex items-start">
                        <FaCheck className="mr-2 mt-0.5 flex-shrink-0" />
                        <span><strong>Numéro de téléphone</strong> pour validation</span>
                      </li>
                      <li className="flex items-start">
                        <FaCheck className="mr-2 mt-0.5 flex-shrink-0" />
                        <span><strong>Code unique</strong> envoyé par email à l'inscription</span>
                      </li>
                    </ul>
                    <p className="text-xs text-green-600 mt-2">
                      Accès démo de 24h, renouvelable sur demande.
                    </p>
                  </div>

                  {educatorError && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded mb-4">
                      {educatorError}
                    </div>
                  )}

                  <form onSubmit={handleEducatorSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block font-bold mb-1">
                          <FaUser className="inline mr-1" /> Prénom *
                        </label>
                        <input
                          type="text"
                          value={educatorForm.firstName}
                          onChange={(e) => setEducatorForm({ ...educatorForm, firstName: e.target.value })}
                          className="w-full p-3 border-2 border-black rounded"
                          placeholder="Prénom"
                          required
                          data-testid="educator-firstname"
                        />
                      </div>
                      <div>
                        <label className="block font-bold mb-1">
                          Nom *
                        </label>
                        <input
                          type="text"
                          value={educatorForm.lastName}
                          onChange={(e) => setEducatorForm({ ...educatorForm, lastName: e.target.value })}
                          className="w-full p-3 border-2 border-black rounded"
                          placeholder="Nom"
                          required
                          data-testid="educator-lastname"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block font-bold mb-1">Fonction *</label>
                      <select
                        value={educatorForm.function}
                        onChange={(e) => setEducatorForm({ ...educatorForm, function: e.target.value })}
                        className="w-full p-3 border-2 border-black rounded"
                        required
                      >
                        <option value="professor">Professeur</option>
                        <option value="director">Directeur d'établissement</option>
                        <option value="pedagogical_director">Responsable pédagogique</option>
                        <option value="other">Autre</option>
                      </select>
                    </div>

                    <div>
                      <label className="block font-bold mb-1">
                        <FaEnvelope className="inline mr-1" /> Email professionnel *
                      </label>
                      <input
                        type="email"
                        value={educatorForm.email}
                        onChange={(e) => setEducatorForm({ ...educatorForm, email: e.target.value })}
                        className="w-full p-3 border-2 border-black rounded"
                        placeholder="vous@etablissement.fr"
                        required
                        data-testid="educator-email"
                      />
                    </div>

                    <div>
                      <label className="block font-bold mb-1">
                        <FaPhone className="inline mr-1" /> Téléphone *
                      </label>
                      <input
                        type="tel"
                        value={educatorForm.phone}
                        onChange={(e) => setEducatorForm({ ...educatorForm, phone: e.target.value })}
                        className="w-full p-3 border-2 border-black rounded"
                        placeholder="06 XX XX XX XX"
                        required
                        data-testid="educator-phone"
                      />
                      <p className="text-xs text-gray-500 mt-1">Requis pour la vérification de votre compte</p>
                    </div>

                    <div>
                      <label className="block font-bold mb-1">
                        <FaSchool className="inline mr-1" /> Nom de l'établissement *
                      </label>
                      <input
                        type="text"
                        value={educatorForm.schoolName}
                        onChange={(e) => setEducatorForm({ ...educatorForm, schoolName: e.target.value })}
                        className="w-full p-3 border-2 border-black rounded"
                        placeholder="Ex: ISCPA Lyon"
                        required
                        data-testid="educator-school"
                      />
                    </div>
                    
                    <div>
                      <label className="block font-bold mb-1">
                        Adresse de l'établissement
                      </label>
                      <input
                        type="text"
                        value={educatorForm.schoolAddress}
                        onChange={(e) => setEducatorForm({ ...educatorForm, schoolAddress: e.target.value })}
                        className="w-full p-3 border-2 border-black rounded"
                        placeholder="Ex: 47 rue Sergent Michel Berthet, 69009 Lyon"
                        data-testid="educator-school-address"
                      />
                    </div>
                    
                    <div>
                      <label className="block font-bold mb-1">
                        Numéro d'agrément (facultatif)
                      </label>
                      <input
                        type="text"
                        value={educatorForm.accreditationNumber}
                        onChange={(e) => setEducatorForm({ ...educatorForm, accreditationNumber: e.target.value })}
                        className="w-full p-3 border-2 border-black rounded"
                        placeholder="Numéro d'agrément du ministère"
                        data-testid="educator-accreditation"
                      />
                    </div>

                    <div>
                      <label className="block font-bold mb-1">
                        <FaLock className="inline mr-1" /> Mot de passe *
                      </label>
                      <input
                        type="password"
                        value={educatorForm.password}
                        onChange={(e) => setEducatorForm({ ...educatorForm, password: e.target.value })}
                        className="w-full p-3 border-2 border-black rounded"
                        placeholder="Minimum 6 caractères"
                        minLength={6}
                        required
                        data-testid="educator-password"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={educatorSubmitting}
                      className="comic-button w-full text-lg py-3"
                      style={{ background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)', color: 'white' }}
                      data-testid="educator-submit"
                    >
                      {educatorSubmitting ? 'Création du compte...' : 'Créer mon accès démo'}
                    </button>

                    <p className="text-xs text-gray-500 text-center">
                      En créant un compte, vous acceptez nos conditions d'utilisation.
                    </p>
                  </form>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default LandingPage;
